import { Controller } from "@hotwired/stimulus"
import Drawer from "flowbite/lib/esm/components/drawer"
import { gsap } from "gsap"

export default class extends Controller {
  static targets = ["item", "container"]

  connect() {
    this.isAdminSidebar = this.element.dataset.sidebarVariant === "admin"
    if (!this.isAdminSidebar) this.animateEntrance()
    this.setupHoverAnimations()
    this.setupDrawer()
    this.restoreFocusAfterNavigation = this.restoreFocusAfterNavigation.bind(this)
    document.addEventListener("turbo:load", this.restoreFocusAfterNavigation)

    if (!this.isAdminSidebar) {
      // The public sidebar stays mounted while its main frame navigates.
      this.updateActiveLink()
      this.updateActiveLink = this.updateActiveLink.bind(this)
      document.addEventListener("turbo:load", this.updateActiveLink)
      document.addEventListener("turbo:frame-render", this.updateActiveLink)
      window.addEventListener("popstate", this.updateActiveLink)
    }

    this.closeMobileDrawer = this.closeMobileDrawer.bind(this)
    document.addEventListener("turbo:before-cache", this.closeMobileDrawer)
    document.addEventListener("turbo:frame-render", this.closeMobileDrawer)
    document.addEventListener("turbo:frame-load", this.closeMobileDrawer)
  }

  disconnect() {
    if (!this.isAdminSidebar) {
      document.removeEventListener("turbo:load", this.updateActiveLink)
      document.removeEventListener("turbo:frame-render", this.updateActiveLink)
      window.removeEventListener("popstate", this.updateActiveLink)
    }
    document.removeEventListener("turbo:before-cache", this.closeMobileDrawer)
    document.removeEventListener("turbo:frame-render", this.closeMobileDrawer)
    document.removeEventListener("turbo:frame-load", this.closeMobileDrawer)
    document.removeEventListener("turbo:load", this.restoreFocusAfterNavigation)
    this.mediaQuery?.removeEventListener("change", this.syncDrawerState)
    this.toggleButton?.removeEventListener("click", this.toggleDrawer)
    document.removeEventListener("keydown", this.handleFocusTrap)
    this.drawer?.destroyAndRemoveInstance()
  }

  setupDrawer() {
    this.mediaQuery = window.matchMedia("(min-width: 768px)")
    this.syncDrawerState = this.syncDrawerState.bind(this)
    this.toggleDrawer = this.toggleDrawer.bind(this)
    this.toggleButton = document.querySelector(`[data-drawer-toggle="${this.element.id}"]`)
    const drawerOptions = {
      onShow: () => this.handleDrawerShow(),
      onHide: () => this.handleDrawerHide()
    }
    if (this.isAdminSidebar) drawerOptions.backdropClasses = "fixed inset-0 z-30 bg-gray-900 opacity-50"
    this.drawer = new Drawer(this.element, drawerOptions)
    this.toggleButton?.addEventListener("click", this.toggleDrawer)
    this.mediaQuery.addEventListener("change", this.syncDrawerState)
    this.syncDrawerState()
    this.handleFocusTrap = this.handleFocusTrap.bind(this)
    document.addEventListener("keydown", this.handleFocusTrap)
  }

  closeMobileDrawer(event) {
    if (event.type !== "turbo:before-cache" && event.target.id !== "main_content") return

    const restoreFocus = this.restoreFocusOnClose && !this.mediaQuery.matches
    if (event.type === "turbo:before-cache" && restoreFocus) {
      document.documentElement.dataset.sidebarRestoreFocus = "true"
    }
    if (event.type !== "turbo:before-cache" && restoreFocus) this.restoreFocusAfterFrame = true
    if (this.drawer?.isVisible()) this.drawer.hide()
    if (event.type === "turbo:frame-load" && this.restoreFocusAfterFrame) {
      this.restoreFocusAfterFrame = false
      this.toggleButton?.focus()
    }
  }

  restoreFocusAfterNavigation() {
    if (document.documentElement.dataset.sidebarRestoreFocus !== "true") return

    delete document.documentElement.dataset.sidebarRestoreFocus
    this.toggleButton?.focus()
  }

  toggleDrawer(event) {
    event.preventDefault()

    if (!this.mediaQuery.matches) this.drawer.toggle()
  }

  close(event) {
    event.preventDefault()
    this.drawer.hide()
  }

  handleDrawerShow() {
    this.syncDrawerState()
    this.restoreFocusOnClose = !this.mediaQuery.matches
    this.focusableElements()[0]?.focus()
  }

  handleDrawerHide() {
    const restoreFocus = this.restoreFocusOnClose && !this.mediaQuery.matches
    this.restoreFocusOnClose = false
    this.syncDrawerState()
    if (restoreFocus) this.toggleButton?.focus()
  }

  syncDrawerState() {
    if (this.mediaQuery.matches && this.drawer.isVisible()) this.drawer.hide()

    const open = !this.mediaQuery.matches && this.drawer.isVisible()
    const hidden = !this.mediaQuery.matches && !open
    this.element.toggleAttribute("inert", hidden)
    this.element.setAttribute("aria-hidden", String(hidden))
    this.toggleButton?.setAttribute("aria-expanded", String(open))
  }

  handleFocusTrap(event) {
    if (!this.drawer?.isVisible() || event.key !== "Tab") return

    const focusable = this.focusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable.at(-1)
    if (event.shiftKey && (document.activeElement === first || !this.element.contains(document.activeElement))) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (document.activeElement === last || !this.element.contains(document.activeElement))) {
      event.preventDefault()
      first.focus()
    }
  }

  focusableElements() {
    return [ ...this.element.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])") ].filter(element => element.getClientRects().length > 0)
  }

  updateActiveLink() {
    const currentPath = window.location.pathname

    this.itemTargets.forEach(item => {
      const link = item.querySelector('a')
      if (!link) return

      const href = link.getAttribute('href')
      if (!href) return

      const url = new URL(href, window.location.origin)
      if (url.origin !== window.location.origin) return

      const isActive = url.pathname === currentPath
      link.classList.toggle("bg-dodger-blue-500", isActive)
      link.classList.toggle('text-white', isActive)
      link.classList.toggle('text-gray-900', !isActive)
      link.classList.toggle('dark:text-white', !isActive)
      if (isActive) {
        link.setAttribute("aria-current", "page")
      } else {
        link.removeAttribute("aria-current")
      }
    })
  }

  animateEntrance() {
    gsap.fromTo(
      this.itemTargets,
      {
        opacity: 0,
        x: -30,
        scale: 0.95
      },
      {
        opacity: 1,
        x: 0,
        scale: 1,
        duration: 0.6,
        stagger: 0.08,
        ease: "power3.out",
        clearProps: "all"
      }
    )
  }

  setupHoverAnimations() {
    this.itemTargets.forEach(item => {
      const link = item.querySelector('a')
      if (!link) return

      link.addEventListener('mouseenter', () => {
        gsap.to(link, {
          x: 8,
          duration: 0.3,
          ease: "power2.out"
        })

        const icon = link.querySelector('svg')
        if (icon) {
          gsap.to(icon, {
            scale: 1.15,
            rotation: 5,
            duration: 0.3,
            ease: "back.out(1.7)"
          })
        }
      })

      link.addEventListener('mouseleave', () => {
        gsap.to(link, {
          x: 0,
          duration: 0.3,
          ease: "power2.out"
        })

        const icon = link.querySelector('svg')
        if (icon) {
          gsap.to(icon, {
            scale: 1,
            rotation: 0,
            duration: 0.3,
            ease: "power2.out"
          })
        }
      })
    })
  }
}
